// Totals for the Mileage / Strength tiles on the dashboard.
//
// The per-meso values arrive already rounded to one decimal, but adding them in
// JS reintroduces float noise ([68.8, 64.4, 107.2, 165.9] sums to
// 406.29999999999995), and the tiles render the total verbatim. Round the
// totals the same way the percentages are rounded.

export interface ActivityRow {
  category: string;
  planned: number | string | null;
  completed: number | string | null;
}

export interface CategoryTotals {
  percent: number;
  planned: number;
  completed: number;
}

export interface ActivityTotals {
  mileage: CategoryTotals;
  strength: CategoryTotals;
}

// One decimal place, kept numeric: 4 stays 4 (strength sessions are whole
// counts rendered by the same component), 406.29999999999995 becomes 406.3.
export const toOneDecimal = (value: number): number => Math.round(value * 10) / 10;

const num = (value: number | string | null): number =>
  typeof value === 'number' ? value : parseFloat(String(value ?? '0')) || 0;

export function summariseActivity(rows: ActivityRow[]): ActivityTotals {
  let mileagePlanned = 0;
  let mileageCompleted = 0;
  let strengthPlanned = 0;
  let strengthCompleted = 0;

  for (const row of rows) {
    const planned = num(row.planned);
    const completed = num(row.completed);

    if (row.category === 'Mileage') {
      mileagePlanned += planned;
      mileageCompleted += completed;
    }
    if (row.category === 'Strength') {
      strengthPlanned += planned;
      strengthCompleted += completed;
    }
  }

  const percent = (completed: number, planned: number) =>
    planned > 0 ? toOneDecimal((completed / planned) * 100) : 0;

  return {
    mileage: {
      percent: percent(mileageCompleted, mileagePlanned),
      planned: toOneDecimal(mileagePlanned),
      completed: toOneDecimal(mileageCompleted),
    },
    strength: {
      percent: percent(strengthCompleted, strengthPlanned),
      planned: toOneDecimal(strengthPlanned),
      completed: toOneDecimal(strengthCompleted),
    },
  };
}
